import { Badge, Center, Group, Loader, Modal, Paper, Stack, Text } from '@mantine/core';
import { IconHistory } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { DeadlineChangeEntry } from '../../lib/api/domains/customerDeadlines';
import { DEADLINE_CATEGORY_LABELS, formatIsoDateDe, type CustomerDeadlineCategory } from '../../lib/customerDeadlines';

const FIELD_LABELS: Record<string, string> = {
  name: 'Bezeichnung',
  category: 'Kategorie',
  ownership: 'Inhaber',
  provider: 'Anbieter',
  reference: 'Referenz',
  quantity: 'Menge',
  startDate: 'Laufzeitbeginn',
  termType: 'Laufzeit',
  endDate: 'Laufzeitende',
  autoRenew: 'Auto-Verlängerung',
  renewalMonths: 'Verlängerung (Monate)',
  noticeDays: 'Kündigungsfrist (Tage)',
  noticeAnchor: 'Fristbezug',
  terminationPlanned: 'Gekündigt',
  customerNote: 'Hinweis für den Kunden',
  internalNote: 'Interne Notiz',
  visibleInPortal: 'Im Portal sichtbar',
  isActive: 'Aktiv',
};

const ACTION_META: Record<string, { label: string; color: string }> = {
  CREATED: { label: 'angelegt', color: 'teal' },
  UPDATED: { label: 'geändert', color: 'blue' },
  ARCHIVED: { label: 'archiviert', color: 'gray' },
  RESTORED: { label: 'reaktiviert', color: 'teal' },
  DELETED: { label: 'gelöscht', color: 'red' },
  REQUEST_OPENED: { label: 'Kundenanfrage', color: 'grape' },
  REQUEST_RESOLVED: { label: 'Anfrage erledigt', color: 'grape' },
  REMINDER_TICKET: { label: 'Erinnerungsticket', color: 'orange' },
  PROOF_ADDED: { label: 'Beleg hinzugefügt', color: 'cyan' },
  PROOF_UPDATED: { label: 'Beleg-Freigabe', color: 'cyan' },
  PROOF_REMOVED: { label: 'Beleg entfernt', color: 'red' },
};

const SOURCE_LABELS: Record<string, string> = {
  TECHNICIAN: 'ServiceHub',
  CUSTOMER: 'Kundenportal',
  IMPORT: 'Import',
  SYSTEM: 'Automatik',
};

const OWNERSHIP_TEXT: Record<string, string> = {
  FIXBYTE: 'FixByte stellt bereit',
  CUSTOMER_MANAGED: 'Kunde · FixByte verwaltet',
};

const TERM_TEXT: Record<string, string> = {
  FIXED: 'eigene Laufzeit',
  PENDING: 'Laufzeit angefragt',
  SERVICE_CONTRACT: 'mit FixByte-Servicevertrag',
};

const ANCHOR_TEXT: Record<string, string> = {
  END_DATE: 'zum Laufzeitende',
  MONTH_END: 'zum Monatsende',
};

/** Bei Anlage und Löschen die Eckdaten statt aller Felder — sonst geht unter, was zählt. */
const KEY_FIELDS = ['category', 'ownership', 'provider', 'termType', 'endDate'];

function formatValue(field: string, value: string | number | boolean | null): string {
  if (value === null) return '—';
  if (typeof value === 'boolean') return value ? 'ja' : 'nein';
  if (field === 'category') return DEADLINE_CATEGORY_LABELS[value as CustomerDeadlineCategory] ?? String(value);
  if (field === 'ownership') return OWNERSHIP_TEXT[String(value)] ?? String(value);
  if (field === 'noticeAnchor') return ANCHOR_TEXT[String(value)] ?? String(value);
  if (field === 'termType') return TERM_TEXT[String(value)] ?? String(value);
  if (field === 'startDate' || field === 'endDate') return formatIsoDateDe(String(value));
  return String(value);
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ChangeLines({ entry }: { entry: DeadlineChangeEntry }) {
  if (entry.action === 'CREATED' || entry.action === 'DELETED') {
    const side = entry.action === 'CREATED' ? 'to' : 'from';
    // „eigene Laufzeit" ist der Normalfall, ein leeres Laufzeitende beim Managed Service selbstverständlich.
    const keyChanges = entry.changes.filter(
      (c) =>
        KEY_FIELDS.includes(c.field) &&
        !(c.field === 'termType' && c[side] === 'FIXED') &&
        !(c.field === 'endDate' && c[side] === null)
    );
    if (keyChanges.length === 0) return null;
    return (
      <Text size="xs" c="dimmed">
        {keyChanges.map((c) => `${FIELD_LABELS[c.field] ?? c.field}: ${formatValue(c.field, c[side])}`).join(' · ')}
      </Text>
    );
  }
  if (entry.changes.length === 0) return null;
  return (
    <Stack gap={2}>
      {entry.changes.map((c) => (
        <Text key={c.field} size="xs" style={{ overflowWrap: 'anywhere' }}>
          <Text span size="xs" c="dimmed">
            {FIELD_LABELS[c.field] ?? c.field}:{' '}
          </Text>
          <Text span size="xs" c="dimmed" td="line-through">
            {formatValue(c.field, c.from)}
          </Text>
          {' → '}
          <Text span size="xs" fw={500}>
            {formatValue(c.field, c.to)}
          </Text>
        </Text>
      ))}
    </Stack>
  );
}

/**
 * Änderungsprotokoll für Verträge & Lizenzen — für einen Posten oder den ganzen Kunden
 * (dann auch für inzwischen gelöschte Posten).
 */
export function DeadlineChangeLogModal({
  opened,
  onClose,
  customerId,
  deadline,
}: {
  opened: boolean;
  onClose: () => void;
  customerId: string;
  /** null = alle Änderungen beim Kunden */
  deadline: { id: string; name: string } | null;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['customer-deadline-changes', customerId, deadline?.id ?? 'all'],
    queryFn: () => api.getCustomerDeadlineChanges(customerId, deadline?.id),
    enabled: opened,
  });
  const entries = data?.changes ?? [];

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      zIndex={800}
      size="lg"
      centered
      title={
        <Group gap="xs" wrap="nowrap">
          <IconHistory size={18} />
          <Text fw={600}>{deadline ? `Verlauf — ${deadline.name}` : 'Änderungsprotokoll — Verträge & Lizenzen'}</Text>
        </Group>
      }
    >
      {isLoading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : isError ? (
        <Text size="sm" c="red">
          Das Protokoll konnte nicht geladen werden.
        </Text>
      ) : entries.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="lg">
          Noch keine Einträge. Protokolliert wird seit Version 1.324.0 — frühere Änderungen stehen hier nicht.
        </Text>
      ) : (
        <Stack gap="xs">
          {entries.map((entry) => {
            const meta = ACTION_META[entry.action] ?? { label: entry.action, color: 'gray' };
            return (
              <Paper key={entry.id} withBorder radius="sm" p="xs">
                <Group justify="space-between" gap="xs" wrap="nowrap" align="flex-start" mb={4}>
                  <Group gap={6} wrap="wrap" style={{ minWidth: 0 }}>
                    <Badge size="xs" variant="light" color={meta.color}>
                      {meta.label}
                    </Badge>
                    {!deadline && (
                      <Text size="sm" fw={600} style={{ overflowWrap: 'anywhere' }}>
                        {entry.deadlineName}
                      </Text>
                    )}
                  </Group>
                  <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                    {formatWhen(entry.createdAt)}
                  </Text>
                </Group>
                <Text size="xs" c="dimmed" mb={4}>
                  {SOURCE_LABELS[entry.source] ?? entry.source}
                  {entry.actor ? ` · ${entry.actor}` : ''}
                </Text>
                <ChangeLines entry={entry} />
                {entry.note && (
                  <Text size="xs" mt={4}>
                    {entry.note}
                  </Text>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}
    </Modal>
  );
}
